/**
 * Staff administration.
 *
 * Replaces the previous procedure for granting access, which was: SSH to the
 * VPS and run a script that writes a password hash into the database. That
 * worked exactly once, for the first owner.
 *
 * Two things about this screen are deliberate and easy to undo by accident:
 *
 * 1. **The invite link is shown once, and only here.** The API returns the
 *    plaintext token in the create/resend response and stores only its hash, so
 *    there is no route that can show it again. Resending issues a NEW link and
 *    invalidates the old one. The UI has to make copying it feel like the point
 *    of the step, because closing the dialog really does discard it.
 *
 * 2. **A password is never typed by an administrator.** They send a link; the
 *    person chooses their own password. That is what makes the actor id in the
 *    audit log mean something — if a manager could set a colleague's password,
 *    every entry attributed to that colleague would be deniable.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useVenues } from '../hooks/useVenue.js';

type Role =
  | 'owner'
  | 'group_admin'
  | 'venue_manager'
  | 'host'
  | 'server'
  | 'marketing'
  | 'call_centre';

const ROLE_KEYS: Role[] = [
  'owner',
  'group_admin',
  'venue_manager',
  'host',
  'server',
  'marketing',
  'call_centre',
];

interface StaffVenue {
  venue_id: string;
  venue_name: string;
  role: Role;
}

interface StaffMember {
  id: string;
  email: string;
  name: string;
  locale: 'en' | 'ar';
  is_active: boolean;
  last_login_at: string | null;
  venues: StaffVenue[];
  status: 'active' | 'invited' | 'disabled' | 'no_access';
}

export function UsersScreen() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user: me } = useSession();
  const { data: venues } = useVenues();

  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [editing, setEditing] = useState<StaffMember | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: StaffMember[] }>('/api/v1/users'),
  });

  const permissions = useQuery({
    queryKey: ['permissions'],
    queryFn: () =>
      api.get<{
        permissions: string[];
        roles: { role: Role; permissions: string[]; grantable: boolean }[];
      }>('/api/v1/permissions'),
    enabled: showPermissions,
  });

  /** Replace someone's whole role set — which venues, and what at each. */
  const setAccess = useMutation({
    mutationFn: ({ id, venues }: { id: string; venues: { venue_id: string; role: Role }[] }) =>
      api.patch(`/api/v1/users/${id}`, { venues }),
    onSuccess: () => {
      setEditing(null);
      setError(null);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const showLink = (url: string) => {
    setInviteLink(url);
    setCopied(false);
  };

  const create = useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      locale: string;
      venues: { venue_id: string; role: Role }[];
    }) => api.post<{ invite_url: string }>('/api/v1/users', body),
    onSuccess: (res) => {
      setShowAdd(false);
      setError(null);
      showLink(res.invite_url);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const resend = useMutation({
    mutationFn: (id: string) => api.post<{ invite_url: string }>(`/api/v1/users/${id}/invite`),
    onSuccess: (res) => showLink(res.invite_url),
    onError: (e: Error) => setError(e.message),
  });

  const setActive = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      api.patch(`/api/v1/users/${id}`, { is_active }),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    // The API refuses self-disable and last-owner removal with a 409 and a
    // specific message. Surfacing that message beats a generic failure, because
    // both are almost always a mistake rather than an attempt.
    onError: (e: Error) => setError(e.message),
  });

  const venueOptions = useMemo(
    () =>
      (venues ?? []).map((v) => ({
        id: v.id,
        name: i18n.language === 'ar' ? v.name_ar : v.name_en,
      })),
    [venues, i18n.language],
  );

  if (isLoading) {
    return <p className="text-sm text-slate-500">{t('app.loading')}</p>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{t('users.title')}</h1>
        <button
          type="button"
          onClick={() => setShowPermissions((v) => !v)}
          className="ms-auto rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          {t('users.whatRolesCanDo')}
        </button>
        <button
          type="button"
          onClick={() => {
            setShowAdd(true);
            setError(null);
          }}
          className="rounded-md bg-brand-teal px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-teal-dark"
        >
          {t('users.invite')}
        </button>
      </div>

      {error ? (
        <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {inviteLink ? <InviteLinkPanel url={inviteLink} copied={copied} onCopied={setCopied} onClose={() => setInviteLink(null)} /> : null}

      {showPermissions ? (
        <RoleMatrix
          data={permissions.data}
          loading={permissions.isLoading}
        />
      ) : null}

      {editing ? (
        <AccessEditor
          member={editing}
          venues={venueOptions}
          pending={setAccess.isPending}
          onCancel={() => setEditing(null)}
          onSave={(venues) => setAccess.mutate({ id: editing.id, venues })}
        />
      ) : null}

      {showAdd ? (
        <AddStaffForm
          venues={venueOptions}
          pending={create.isPending}
          onCancel={() => setShowAdd(false)}
          onSubmit={(body) => create.mutate(body)}
        />
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-slate-500">
            <tr>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('users.name')}</th>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('users.access')}</th>
              <th scope="col" className="px-4 py-2 text-start font-medium">{t('users.status')}</th>
              <th scope="col" className="px-4 py-2 text-end font-medium">{t('users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.users ?? []).map((u) => (
              <tr key={u.id} className="border-b border-slate-100 last:border-0">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{u.name}</div>
                  <div className="text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <ul className="space-y-0.5">
                    {u.venues.map((v) => (
                      <li key={v.venue_id} className="text-slate-700">
                        {v.venue_name} · {t(`roles.${v.role}`)}
                      </li>
                    ))}
                  </ul>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={u.status} />
                </td>
                <td className="px-4 py-3 text-end">
                  <div className="flex flex-wrap justify-end gap-2">
                    {u.status === 'invited' || u.status === 'no_access' ? (
                      <button
                        type="button"
                        onClick={() => resend.mutate(u.id)}
                        className="rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50"
                      >
                        {t('users.resend')}
                      </button>
                    ) : null}
                    {/* No self-disable button. The API refuses it anyway, but
                        offering a control that always fails is worse than not
                        offering it. */}
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(u);
                        setError(null);
                      }}
                      className="rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50"
                    >
                      {t('users.editAccess')}
                    </button>
                    {u.id === me?.id ? (
                      <span className="text-slate-400">{t('users.you')}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setActive.mutate({ id: u.id, is_active: !u.is_active })}
                        className="rounded-md border border-slate-200 px-2 py-1 text-slate-600 hover:bg-slate-50"
                      >
                        {u.is_active ? t('users.disable') : t('users.enable')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: StaffMember['status'] }) {
  const { t } = useTranslation();
  const tone =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'invited'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-slate-100 text-slate-600';
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}>
      {t(`users.status_${status}`)}
    </span>
  );
}

/**
 * The link, shown once.
 *
 * Loud on purpose. The token is not recoverable — the server holds only its
 * hash — so a panel that looked like a passive confirmation would routinely be
 * dismissed before anyone copied anything.
 */
function InviteLinkPanel({
  url,
  copied,
  onCopied,
  onClose,
}: {
  url: string;
  copied: boolean;
  onCopied: (v: boolean) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border-2 border-brand-bronze bg-brand-bronze-light p-4">
      <h2 className="font-semibold text-brand-bronze-dark">{t('users.linkReady')}</h2>
      <p className="mt-1 text-sm text-slate-700">{t('users.linkOnce')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md bg-white px-3 py-2 text-xs text-slate-800">
          {url}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => onCopied(true));
          }}
          className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark"
        >
          {copied ? t('users.copied') : t('users.copy')}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-white"
        >
          {t('users.done')}
        </button>
      </div>
    </section>
  );
}

function AddStaffForm({
  venues,
  pending,
  onCancel,
  onSubmit,
}: {
  venues: { id: string; name: string }[];
  pending: boolean;
  onCancel: () => void;
  onSubmit: (body: {
    email: string;
    name: string;
    locale: string;
    venues: { venue_id: string; role: Role }[];
  }) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [locale, setLocale] = useState('en');
  const [venueId, setVenueId] = useState(venues[0]?.id ?? '');
  const [role, setRole] = useState<Role>('host');

  return (
    <form
      className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ email, name, locale, venues: [{ venue_id: venueId, role }] });
      }}
    >
      <label className="text-sm">
        <span className="mb-1 block text-slate-600">{t('users.name')}</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-600">{t('users.email')}</span>
        <input
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-600">{t('users.venue')}</span>
        <select
          value={venueId}
          onChange={(e) => setVenueId(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        >
          {venues.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-600">{t('users.role')}</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        >
          {ROLE_KEYS.map((r) => (
            <option key={r} value={r}>
              {t(`roles.${r}`)}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        <span className="mb-1 block text-slate-600">{t('users.language')}</span>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2"
        >
          <option value="en">{t('users.english')}</option>
          <option value="ar">{t('users.arabic')}</option>
        </select>
      </label>

      <div className="flex items-end gap-2">
        <button
          type="submit"
          disabled={pending}
          className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
        >
          {t('users.sendInvite')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          {t('users.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * What each role can do, as a table.
 *
 * Read-only, because the API is: role definitions are compiled in and enforced
 * there. A permission set editable in production is one that gets widened at
 * 2am so a colleague can see a report, with the audit log recording the change
 * but never the reasoning.
 *
 * It exists so that granting a role is an informed act. "Host" tells a manager
 * nothing; a column of ticks tells them exactly who will be able to read a
 * guest's phone number.
 */
function RoleMatrix({
  data,
  loading,
}: {
  data?: { permissions: string[]; roles: { role: Role; permissions: string[]; grantable: boolean }[] };
  loading: boolean;
}) {
  const { t } = useTranslation();
  if (loading || !data) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <section className="overflow-x-auto rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="font-semibold">{t('users.whatRolesCanDo')}</h2>
      <p className="mt-1 text-sm text-slate-600">{t('users.rolesReadOnly')}</p>

      <table className="mt-4 w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-slate-500">
            <th scope="col" className="py-2 text-start font-medium">{t('users.permission')}</th>
            {data.roles.map((r) => (
              <th key={r.role} scope="col" className="px-2 py-2 text-center font-medium">
                <span className="block">{t(`roles.${r.role}`)}</span>
                {/* Says up front which roles this person may hand out, so a
                    refusal is explained before it happens rather than after. */}
                {!r.grantable ? (
                  <span className="block text-[10px] font-normal text-amber-700">
                    {t('users.ownerOnly')}
                  </span>
                ) : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.permissions.map((permission) => (
            <tr key={permission} className="border-b border-slate-100 last:border-0">
              <th scope="row" className="py-1.5 text-start font-mono text-xs font-normal text-slate-700">
                {permission}
              </th>
              {data.roles.map((r) => (
                <td key={r.role} className="px-2 py-1.5 text-center">
                  {r.permissions.includes(permission) ? (
                    <span className="text-brand-teal" aria-label={t('users.allowed')}>
                      {'✓' /* i18n-ok: a tick */}
                    </span>
                  ) : (
                    <span className="text-slate-300" aria-label={t('users.notAllowed')}>
                      {'–' /* i18n-ok: an en dash */}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Which restaurants someone can see, and what they may do at each.
 *
 * A role per venue, not one role for the person: a manager at West Bay who
 * helps out as a host at The Pearl is one account with two different levels of
 * authority, and collapsing that to a single role would over-grant them
 * somewhere.
 *
 * Removing every venue is not offered. An account with no venues can sign in
 * and see nothing, which looks like a broken app rather than revoked access —
 * disabling the account is the honest way to say that.
 */
function AccessEditor({
  member,
  venues,
  pending,
  onCancel,
  onSave,
}: {
  member: StaffMember;
  venues: { id: string; name: string }[];
  pending: boolean;
  onCancel: () => void;
  onSave: (venues: { venue_id: string; role: Role }[]) => void;
}) {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState<Record<string, Role | ''>>(() => {
    const initial: Record<string, Role | ''> = {};
    for (const v of venues) {
      initial[v.id] = member.venues.find((mv) => mv.venue_id === v.id)?.role ?? '';
    }
    return initial;
  });

  const chosen = Object.entries(assigned)
    .filter(([, role]) => role !== '')
    .map(([venue_id, role]) => ({ venue_id, role: role as Role }));

  return (
    <section className="rounded-lg border-2 border-brand-teal bg-brand-teal-light/30 p-4">
      <h2 className="font-semibold">{t('users.accessFor', { name: member.name })}</h2>
      <p className="mt-1 text-sm text-slate-700">{t('users.accessHelp')}</p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {venues.map((v) => (
          <label key={v.id} className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">{v.name}</span>
            <select
              value={assigned[v.id] ?? ''}
              onChange={(e) =>
                setAssigned((current) => ({ ...current, [v.id]: e.target.value as Role | '' }))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-2"
            >
              <option value="">{t('users.noAccess')}</option>
              {ROLE_KEYS.map((r) => (
                <option key={r} value={r}>
                  {t(`roles.${r}`)}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={pending || chosen.length === 0}
          onClick={() => onSave(chosen)}
          className="rounded-md bg-brand-teal px-3 py-2 text-sm font-medium text-white hover:bg-brand-teal-dark disabled:opacity-50"
        >
          {t('users.saveAccess')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >
          {t('users.cancel')}
        </button>
        {chosen.length === 0 ? (
          <span className="text-xs text-amber-700">{t('users.needsOneVenue')}</span>
        ) : null}
      </div>
    </section>
  );
}
